/*
  Compatibility checker.

  Looks up the chosen Make/Model/Year in the versioned 1996+ catalog built by
  .github/scripts/build-compat-data.py. Membership and coverage are separate:
  eligible non-EV 2008+ vehicles have baseline support; older vehicles and EVs
  require exact IOSiX coverage. Uncovered EVs stay selectable but unsupported.
  Market exclusions and the EV support policy
  are applied by the builder before baseline support, not inferred in the browser.
  A missing or malformed artifact is an error, never a limited-support result.

  FLAGGED FOR CLAIMS REVIEW: the exact wording of the two verdicts below is
  pinned copy. Do not reword without the same sign-off that copy needs.

  The data file is fetched lazily, on the first interaction with a panel's
  Make select, not at page load — this section sits on the homepage and must
  not add page weight for visitors who never touch the checker. Fetches are
  cached per source URL so multiple panels on one page (or repeat
  interactions) only fetch once.
*/
(function () {
  var VERDICT_SUPPORTED =
    "Good news: Miles works with your car. Membership required to activate.";
  var VERDICT_LIMITED =
    "Miles works with your car for location, trips, and crash response — some vehicle-health data may be limited on this model.";
  var VERDICT_UNSUPPORTED = "Miles doesn't currently support this vehicle.";
  var LOAD_FAILED =
    "We couldn't load the vehicle list. Try again in a moment, or tell us what you drive below.";
  // Same message for a checker without the "Don't see your car?" form, which
  // is every checker while that form is switched off — "below" would point at
  // nothing.
  var LOAD_FAILED_NO_FORM = "We couldn't load the vehicle list. Try again in a moment.";

  var dataCache = {};
  // Shared across panels; no cookie/storage and no replay after consent changes.
  var selectedVehicles = Object.create(null);

  function validateData(data) {
    if (!data || data.schema_version !== 2 || !data.vehicles ||
        typeof data.vehicles !== "object" || Array.isArray(data.vehicles) ||
        typeof data.catalog_version !== "string" || !data.catalog_version ||
        typeof data.coverage_version !== "string" || !data.coverage_version) {
      throw new Error("miles-compat: invalid catalog version");
    }
    var count = 0;
    Object.keys(data.vehicles).forEach(function (make) {
      var models = data.vehicles[make];
      if (!models || typeof models !== "object" || Array.isArray(models) || !Object.keys(models).length) {
        throw new Error("miles-compat: invalid make");
      }
      Object.keys(models).forEach(function (model) {
        var years = models[model];
        if (!years || typeof years !== "object" || Array.isArray(years) || !Object.keys(years).length) {
          throw new Error("miles-compat: invalid model");
        }
        Object.keys(years).forEach(function (year) {
          var entry = years[year];
          var reasons = { supported: ["iosix_signals", "model_year_baseline"], limited: ["iosix_no_signals"], unsupported: ["no_iosix_match", "ev_not_approved"] };
          if (!/^\d{4}$/.test(year) || Number(year) < 1996 || !entry ||
              typeof entry.id !== "string" || !entry.id ||
              !has(reasons, entry.verdict) || reasons[entry.verdict].indexOf(entry.reason) === -1 ||
              (entry.reason === "model_year_baseline" && Number(year) < 2008) ||
              (entry.powertrain !== undefined && entry.powertrain !== "electric") ||
              (entry.powertrain === "electric" && (["iosix_signals", "iosix_no_signals", "ev_not_approved"].indexOf(entry.reason) === -1 || entry.electric_version)) ||
              (entry.reason === "ev_not_approved" && entry.powertrain !== "electric") ||
              typeof entry.electric_version !== "boolean" ||
              (entry.variant !== undefined && typeof entry.variant !== "string")) {
            throw new Error("miles-compat: invalid vehicle");
          }
          count++;
        });
      });
    });
    if (!count) throw new Error("miles-compat: empty catalog");
    return data;
  }

  function loadData(src) {
    if (!dataCache[src]) {
      dataCache[src] = fetch(src, { credentials: "same-origin" }).then(function (response) {
        if (!response.ok) throw new Error("miles-compat: bad response " + response.status);
        return response.json();
      }).then(validateData);
      // Forget a failed fetch so the next focus tries again.
      dataCache[src].catch(function () {
        delete dataCache[src];
        // One event per failed shared request, including failures before a
        // vehicle can be selected. Fixed labels only; no URL or error text.
        track("compatibility load failed", { verdict: "error" });
      });
    }
    return dataCache[src];
  }

  // "Mazda3" before "Mazda6", "3500HG" after "1500", and Mini's capitals
  // mixed in with everyone else rather than sorted ahead of them.
  function byName(a, b) {
    return a.localeCompare(b, "en", { numeric: true, sensitivity: "base" });
  }

  function clearSelect(select, placeholder) {
    select.innerHTML = "";
    var option = document.createElement("option");
    option.value = "";
    option.textContent = placeholder;
    select.appendChild(option);
  }

  function fillSelect(select, values, placeholder) {
    clearSelect(select, placeholder);
    for (var i = 0; i < values.length; i++) {
      var option = document.createElement("option");
      option.value = values[i];
      option.textContent = values[i];
      select.appendChild(option);
    }
    select.disabled = values.length === 0;
  }

  function has(object, key) {
    return Boolean(object) && Object.prototype.hasOwnProperty.call(object, key);
  }

  // Analytics, when the page has it. snippets/miles-posthog.liquid is not
  // rendered in the theme editor and steps aside for a browser sending Global
  // Privacy Control, so window.posthog is not a given. Events carry fixed
  // labels from the catalog, never the visitor — no email, no name, no free
  // text.
  function track(name, properties) {
    var posthog = window.posthog;
    if (!posthog || typeof posthog.capture !== "function") return;
    try {
      posthog.capture(name, properties);
    } catch (e) {
      // Analytics must never break the checker.
    }
  }

  function bind(panel) {
    var button = panel.querySelector("[data-compat-check]");
    var result = panel.querySelector("[data-compat-result]");
    var resultText = panel.querySelector("[data-compat-result-text]");
    var caveat = panel.querySelector("[data-compat-caveat]");
    var makeSelect = panel.querySelector('[data-compat-field="make"]');
    var modelSelect = panel.querySelector('[data-compat-field="model"]');
    var yearSelect = panel.querySelector('[data-compat-field="year"]');
    var src = panel.dataset.compatSrc;

    if (!button || !result || !resultText || !caveat || !makeSelect || !modelSelect || !yearSelect || !src) return;

    var makePlaceholder = makeSelect.options[0] ? makeSelect.options[0].textContent : "Select make";
    var modelPlaceholder = modelSelect.options[0] ? modelSelect.options[0].textContent : "Select model";
    var yearPlaceholder = yearSelect.options[0] ? yearSelect.options[0].textContent : "Select year";

    var data = null;
    var catalog = null;
    var loading = false;
    var loadFailed = panel.querySelector("[data-compat-notlisted-form]") ? LOAD_FAILED : LOAD_FAILED_NO_FORM;

    function showVerdict(kind, text, electricVersion) {
      result.dataset.verdict = kind;
      resultText.textContent = text;
      caveat.hidden = !electricVersion;
      result.hidden = false;
    }

    function hideVerdict() {
      result.hidden = true;
    }

    function updateButtonState() {
      button.disabled = !(makeSelect.value && modelSelect.value && yearSelect.value);
    }

    function resetYear() {
      clearSelect(yearSelect, yearPlaceholder);
      yearSelect.disabled = true;
    }

    function onMakeChange() {
      hideVerdict();
      clearSelect(modelSelect, modelPlaceholder);
      modelSelect.disabled = true;
      resetYear();
      if (data && has(data, makeSelect.value)) {
        fillSelect(modelSelect, Object.keys(data[makeSelect.value]).sort(byName), modelPlaceholder);
      }
      updateButtonState();
    }

    function onModelChange() {
      hideVerdict();
      resetYear();
      if (data && has(data, makeSelect.value) && has(data[makeSelect.value], modelSelect.value)) {
        var years = Object.keys(data[makeSelect.value][modelSelect.value]).sort(function (a, b) {
          return Number(b) - Number(a);
        });
        fillSelect(yearSelect, years, yearPlaceholder);
      }
      updateButtonState();
    }

    function onYearChange() {
      hideVerdict();
      updateButtonState();
      var entry = selectedEntry();
      if (!entry) return;
      var key = catalog.catalog_version + ":" + catalog.coverage_version + ":" + entry.id;
      if (selectedVehicles[key]) return;
      selectedVehicles[key] = true;
      track("vehicle selected", eventProperties(entry));
    }

    function selectedEntry() {
      if (!makeSelect.value || !modelSelect.value || !yearSelect.value) return null;
      var models = data && has(data, makeSelect.value) ? data[makeSelect.value] : null;
      var years = has(models, modelSelect.value) ? models[modelSelect.value] : null;
      return has(years, yearSelect.value) ? years[yearSelect.value] : null;
    }

    function eventProperties(entry) {
      var properties = {
        vehicle_id: entry.id,
        make: makeSelect.value,
        model: modelSelect.value,
        year: Number(yearSelect.value),
        verdict: entry.verdict,
        reason: entry.reason,
        electric_version: entry.electric_version,
        catalog_version: catalog.catalog_version,
        coverage_version: catalog.coverage_version
      };
      if (entry.variant) properties.variant = entry.variant;
      if (entry.powertrain) properties.powertrain = entry.powertrain;
      return properties;
    }

    function onCheck() {
      // Guard even though the button is disabled until all three are chosen —
      // never render a verdict from a partial selection.
      if (!makeSelect.value || !modelSelect.value || !yearSelect.value) return;

      var entry = selectedEntry();
      if (!entry) {
        showVerdict("error", loadFailed, false);
        // Nothing was looked up, so nothing about the vehicle is known —
        // and the selects are not catalog-backed on this path.
        track("vehicle checked", { verdict: "error" });
        return;
      }
      var text = { supported: VERDICT_SUPPORTED, limited: VERDICT_LIMITED, unsupported: VERDICT_UNSUPPORTED };
      showVerdict(entry.verdict, text[entry.verdict], entry.electric_version);
      track("vehicle checked", eventProperties(entry));
    }

    function ensureDataLoaded() {
      if (data || loading) return;
      loading = true;
      loadData(src)
        .then(function (loaded) {
          loading = false;
          catalog = loaded;
          data = loaded.vehicles;
          hideVerdict();
          fillSelect(makeSelect, Object.keys(data).sort(byName), makePlaceholder);
          updateButtonState();
        })
        .catch(function () {
          loading = false;
          // Honest failure, said out loud: we cannot look anything up, so say
          // so now rather than leaving dead controls. A transient blip is NOT
          // latched — the next focus retries the fetch.
          showVerdict("error", loadFailed, false);
        });
    }

    // Load when the panel comes into view, not when the Make select is focused.
    //
    // Focus was too late by one click. Clicking a <select> fires focus and opens
    // the dropdown in the same gesture, so the fetch started while the browser
    // was already painting the list — the first click showed nothing but the
    // placeholder, and you had to close it and click again. Loading on
    // intersection keeps the whole point of deferring it (this panel sits on
    // the homepage, which must not pay for the vehicle list at page load) while
    // finishing long before anyone reaches the control. 300px of rootMargin
    // starts it just before the panel is actually on screen.
    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            observer.disconnect();
            ensureDataLoaded();
            return;
          }
        }
      }, { rootMargin: "300px" });
      observer.observe(panel);
    }

    // Kept, and not only for browsers without IntersectionObserver: the catch
    // above deliberately does not latch a failed fetch, and focus is what
    // retries it. The observer has disconnected by then.
    makeSelect.addEventListener("focus", ensureDataLoaded);
    makeSelect.addEventListener("change", onMakeChange);
    modelSelect.addEventListener("change", onModelChange);
    yearSelect.addEventListener("change", onYearChange);
    button.addEventListener("click", onCheck);

    modelSelect.disabled = true;
    yearSelect.disabled = true;
    updateButtonState();

    bindNotListed(panel, { make: makeSelect, model: modelSelect, year: yearSelect });
  }

  // "Don't see your car?" The form posts without any of this; the script only
  // opens and closes it, carries over what the visitor already picked, and
  // keeps what they typed across a failed submit.
  function bindNotListed(panel, selects) {
    var toggle = panel.querySelector("[data-compat-notlisted-toggle]");
    var form = panel.querySelector("[data-compat-notlisted]");
    var formElement = panel.querySelector("[data-compat-notlisted-form]");
    var cancel = panel.querySelector("[data-compat-notlisted-cancel]");
    var sent = panel.querySelector("[data-compat-sent]");
    var error = panel.querySelector("[data-compat-error]");
    if (!toggle || !form) return;

    var inputs = form.querySelectorAll("[data-compat-prefill]");
    // Shopify re-renders a rejected submit with only the email filled back in;
    // it has no way to return custom fields. Keep them for this tab instead.
    var storeKey = "miles-compat-notlisted:" + (formElement ? formElement.id : "");

    function stash() {
      var values = {};
      Array.prototype.forEach.call(inputs, function (input) {
        values[input.dataset.compatPrefill] = input.value;
      });
      try {
        window.sessionStorage.setItem(storeKey, JSON.stringify(values));
      } catch (e) {
        // Storage blocked (private mode, disabled): the form still sends.
      }
    }

    function recall() {
      try {
        return JSON.parse(window.sessionStorage.getItem(storeKey) || "null");
      } catch (e) {
        return null;
      }
    }

    function restore() {
      var values = recall();
      if (!values) return;
      Array.prototype.forEach.call(inputs, function (input) {
        var saved = values[input.dataset.compatPrefill];
        if (!input.value && typeof saved === "string") input.value = saved;
      });
    }

    function forget() {
      try {
        window.sessionStorage.removeItem(storeKey);
      } catch (e) {
        // Nothing to clean up if storage is unavailable.
      }
    }

    function open() {
      form.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
      var firstEmpty = null;
      Array.prototype.forEach.call(inputs, function (input) {
        var select = selects[input.dataset.compatPrefill];
        var picked = select && select.value ? select.options[select.selectedIndex].textContent : "";
        // Refresh what we filled in last time unless the visitor typed over it;
        // otherwise a second open after changing the selects mixes two cars.
        if (!input.value || input.value === input.dataset.compatPrefilled) {
          input.value = picked;
          input.dataset.compatPrefilled = picked;
        }
        if (!firstEmpty && !input.value) firstEmpty = input;
      });
      var email = form.querySelector('input[type="email"]');
      if (!firstEmpty && email && !email.value) firstEmpty = email;
      if (firstEmpty) firstEmpty.focus();
    }

    function close() {
      form.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      toggle.focus();
    }

    toggle.addEventListener("click", function () {
      if (form.hidden) {
        open();
      } else {
        close();
      }
    });
    if (cancel) cancel.addEventListener("click", close);
    if (formElement) formElement.addEventListener("submit", stash);

    if (error) {
      // The inputs now hold the values; the stash has done its job. Kept
      // any longer, a rejected attempt could count as sent if the visitor
      // later reopens an old ?contact_posted=true page from history.
      restore();
      forget();
    }
    if (sent) {
      // Shopify confirmed the send, so count the miss — once. The stash is
      // the token: it is written on submit and cleared here, so reloading
      // the ?contact_posted=true page cannot count it again. With storage
      // blocked there is no stash and no event; that is the honest loss.
      // The event carries no properties: what the visitor typed is free
      // text, and free text never goes to analytics — the support inbox
      // already holds every submission with its vehicle.
      if (recall()) track("vehicle not listed submitted");
      forget();
    }
    // autofocus in the markup covers this without JavaScript; this is for
    // browsers that skip autofocus on a non-form element.
    var status = error || sent;
    if (status && document.activeElement !== status) status.focus();
  }

  function init() {
    var panels = document.querySelectorAll("[data-compat]");
    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.dataset.compatReady === "true") return;
      panel.dataset.compatReady = "true";
      bind(panel);
    });
  }

  init();
  document.addEventListener("shopify:section:load", init);
})();
